import { Pagination } from "@components/pagination";
import { Select } from "@components/forms/select";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getRaffleDefaultCustomerTicketStatus,
  getRaffleStatus,
} from "@hooks/functions/Raffle/status";
import { getRaffleType } from "@hooks/functions/Raffle/type";
import { RaffleDefaultTicketStatusEnum } from "@hooks/enums/raffle_default_ticket";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { ITicketCampaign, ITicketCampaignTicket } from "../types";

interface IProps {
  campaign: ITicketCampaign;
  tickets: ITicketCampaignTicket[];
  total: number;
  totalPages: number;
  page: number;
  take: number;
  order: "DESC" | "ASC";
  setPage: (page: number) => void;
  setTake: (take: number) => void;
  setOrder: (order: "DESC" | "ASC") => void;
  loading?: boolean;
  onClose: () => void;
}

const masks = new Masks();

const takeOptions = [
  { value: 10, name: "10 registros" },
  { value: 25, name: "25 registros" },
  { value: 50, name: "50 registros" },
  { value: 100, name: "100 registros" },
];

const orderOptions = [
  { value: "DESC", name: "Recentes" },
  { value: "ASC", name: "Antigos" },
];

export function CampaignTicketsModal({
  campaign,
  tickets,
  total,
  totalPages,
  page,
  take,
  order,
  setPage,
  setTake,
  setOrder,
  loading = false,
  onClose,
}: IProps) {
  const [animate, setAnimate] = useState(false);
  const campaignStatus = getRaffleStatus(campaign.status);
  const campaignType = getRaffleType(campaign.type);
  const paidTickets = tickets.filter(
    (ticket) => ticket.status === RaffleDefaultTicketStatusEnum.UNAVAILABLE,
  );
  const pendingTickets = tickets.filter(
    (ticket) => ticket.status === RaffleDefaultTicketStatusEnum.PENDING,
  );

  const summary = [
    {
      label: "Tickets",
      value: total,
      className: "text-dark",
      title: `Quantidade total de tickets em seu usuário.`,
    },
    {
      label: "Adquiridos",
      value: paidTickets.length,
      className: "text-success",
      title: `Quantidade de tickets pagos na relação sendo exibida abaixo.`,
    },
    {
      label: "Pendentes",
      value: pendingTickets.length,
      className: "text-primary",
      title: `Quantidade de tickets pendentes na relação sendo exibida abaixo.`,
    },
  ];

  function handleClose() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  useEffect(() => {
    setTimeout(() => setAnimate(true), 10);

    function handleEscapeClose() {
      setAnimate(false);
      setTimeout(() => onClose(), 300);
    }

    function handleEscape(evt: KeyboardEvent) {
      if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
        handleEscapeClose();
      }
    }

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <>
      <div
        className={`modal fade ${animate ? "show" : ""}`}
        style={{ display: "block" }}
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div
            className="modal-content"
            style={{
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
            }}
          >
            <div
              className="p-3 p-md-4"
              style={{ backgroundColor: "#ffffff", position: "relative" }}
            >
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={handleClose}
                style={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  zIndex: 2,
                }}
              />

              <div style={{ paddingRight: 36 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "96px minmax(0, 1fr)",
                    gap: 16,
                    alignItems: "start",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#f3f4f6",
                      overflow: "hidden",
                      alignSelf: "start",
                    }}
                  >
                    <img
                      src={campaign.thumb}
                      alt={campaign.name}
                      style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>

                  <div style={{ minWidth: 0, textAlign: "left" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "center",
                        justifyContent: "flex-start",
                        marginBottom: 10,
                        textAlign: "left",
                      }}
                    >
                      <span
                        className={`badge bg-${campaignStatus.color} py-2 px-3`}
                        style={{
                          borderRadius: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          width: "auto",
                          flex: "0 0 auto",
                        }}
                        title={campaignStatus.title}
                      >
                        {campaignStatus.text}
                      </span>
                      <span
                        className="badge bg-dark py-2 px-3"
                        style={{
                          borderRadius: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          width: "auto",
                          flex: "0 0 auto",
                        }}
                        title={campaignType.title}
                      >
                        {campaignType.text}
                      </span>
                    </div>

                    <h5 className="modal-title fw-bold mb-2">
                      {campaign.name}
                    </h5>

                    <div
                      style={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <span
                        className="badge bg-light text-muted border"
                        style={{
                          borderRadius: 8,
                          fontWeight: 600,
                          padding: "0.45rem 0.65rem",
                        }}
                        title="Key da Campanha"
                      >
                        #{campaign.key}
                      </span>
                      <span
                        className="badge bg-light text-muted border"
                        style={{
                          borderRadius: 8,
                          fontWeight: 600,
                          padding: "0.45rem 0.65rem",
                        }}
                        title="Slug da Campanha"
                      >
                        #{campaign.campaign}
                      </span>
                    </div>

                    <p
                      className="text-muted mb-0"
                      style={{ fontSize: "0.85rem", lineHeight: 1.45 }}
                    >
                      {campaign.description_short}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-3 px-md-4 pb-3">
              <div className="row g-2">
                {summary.map((item) => (
                  <div
                    className="col-4"
                    key={item.label}
                    title={item.title}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="p-2 text-center h-100"
                      style={{
                        backgroundColor: "#f9fafb",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <span
                        className="d-block"
                        style={{
                          fontSize: "0.7rem",
                          color: "#9ca3af",
                          fontWeight: 600,
                          marginBottom: 2,
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        className={`fw-bold ${item.className}`}
                        style={{ fontSize: "1rem" }}
                      >
                        {masks.formatMilhar(item.value)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-body pt-0 px-3 px-md-4">
              <div className="row g-2 align-items-end mb-3">
                <div className="col-12 col-md-6">
                  <small className="text-muted">
                    Página {page} de {totalPages} página
                    {totalPages > 1 ? `s` : ``} ({masks.formatMilhar(total)}{" "}
                    ticket{total > 1 ? `s` : ``} encontrado
                    {total > 1 ? `s` : ``})
                  </small>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex flex-column flex-sm-row justify-content-md-end gap-2">
                    <div style={{ minWidth: 150 }}>
                      <Select
                        animated
                        label="Registros"
                        className="form-select form-control"
                        aria-label="Quantidade de tickets por página"
                        value={String(take)}
                        options={takeOptions}
                        onChange={(e) => {
                          setTake(Number(e.target.value));
                          setPage(1);
                        }}
                      />
                    </div>
                    <div style={{ minWidth: 150 }}>
                      <Select
                        animated
                        label="Ordenação"
                        className="form-select form-control"
                        aria-label="Ordenação dos tickets"
                        value={order}
                        options={orderOptions}
                        onChange={(e) => {
                          setOrder(e.target.value as "DESC" | "ASC");
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table
                  className="table table-hover align-middle mb-0"
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <thead style={{ backgroundColor: "#f9fafb" }}>
                    <tr>
                      <th scope="col" className="text-nowrap">
                        Ordem
                      </th>
                      <th scope="col" className="text-nowrap">
                        Ticket
                      </th>
                      <th scope="col" className="text-nowrap">
                        Status
                      </th>
                      <th scope="col" className="text-nowrap">
                        Aquisição
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || tickets.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-5">
                          <div className="fw-semibold">
                            {loading
                              ? "Carregando tickets..."
                              : "Nenhum ticket encontrado."}
                          </div>
                          {!loading && (
                            <small className="text-muted">
                              Esta campanha ainda não retornou tickets para o
                              usuário atual.
                            </small>
                          )}
                        </td>
                      </tr>
                    ) : (
                      tickets.map((ticket) => {
                        const ticketStatus =
                          getRaffleDefaultCustomerTicketStatus(ticket.status);

                        return (
                          <tr key={`${campaign.key}-${ticket.order}`}>
                            <td className="text-nowrap">
                              <span className="fw-semibold">
                                #{masks.formatMilhar(ticket.order)}
                              </span>
                            </td>
                            <td>
                              <span
                                className="badge bg-light text-dark border fs-6"
                                style={{ borderRadius: 6 }}
                              >
                                {ticket.ticket}
                              </span>
                            </td>
                            <td className="text-nowrap">
                              <span
                                className={`badge bg-${ticketStatus.color} py-2 px-3`}
                                title={ticketStatus.title}
                                style={{ borderRadius: 6 }}
                              >
                                {ticketStatus.text}
                              </span>
                            </td>
                            <td className="text-nowrap">
                              {ticket.boughtAt
                                ? dayjs(ticket.boughtAt).format(
                                    "DD/MM/YYYY [às] HH:mm",
                                  )
                                : "-"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-3">
                  <Pagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    maxPageShow={5}
                  />
                </div>
              )}
            </div>

            <div className="modal-footer border-0 px-3 px-md-4 pb-3">
              <button
                type="button"
                className="btn btn-secondary px-4"
                onClick={handleClose}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className={`modal-backdrop fade ${animate ? "show" : ""}`}></div>
    </>
  );
}
